import { matchPath } from 'react-router'

/**
 * The property routes' tabs, in display order (CAP-1034). Every tab but
 * Messages is a `?tab=` value on `/properties/:id`; Messages is its own route,
 * `/properties/:id/messages`. Both routes render the same tab row from this
 * list, so moving between them keeps it in place.
 */
export const PROPERTY_PAGE_TABS = [
  { value: 'calendar', label: 'Calendar' },
  { value: 'profile', label: 'Profile' },
  { value: 'ask', label: 'Ask' },
  { value: 'messages', label: 'Messages' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'weekly-emails', label: 'Weekly Emails' },
  { value: 'settings', label: 'Settings' },
] as const

export type PropertyPageTabValue = (typeof PROPERTY_PAGE_TABS)[number]['value']

function isPropertyPageTab(value: string): value is PropertyPageTabValue {
  return PROPERTY_PAGE_TABS.some(tab => tab.value === value)
}

/**
 * Where a tab lives: the property page with its `?tab=`, or the Messages
 * route. A chosen tab is always named, Calendar included: the page's default
 * depends on the property (an onboarding property opens on Profile), so a
 * tab-less URL is "this property's default", not "Calendar" (CAP-1467).
 */
export function propertyTabPath(propertyId: string, tab: string): string {
  const id = encodeURIComponent(propertyId)
  if (tab === 'messages') return `/properties/${id}/messages`
  if (!isPropertyPageTab(tab)) return `/properties/${id}`
  return `/properties/${id}?tab=${tab}`
}

/**
 * Where switching to `nextPropertyId` lands from the current location: the
 * same tab of the property page (`?tab=` kept), or the new property's
 * Messages list. A thread deep link falls back to that list, since a thread
 * belongs to one property.
 */
export function propertySwitchPath(pathname: string, search: string, nextPropertyId: string) {
  if (
    matchPath('/properties/:propertyId/messages', pathname) ||
    matchPath('/properties/:propertyId/messages/:threadId', pathname)
  ) {
    return propertyTabPath(nextPropertyId, 'messages')
  }
  // No tab, or an unknown one, lands on the new property's own default.
  const tab = new URLSearchParams(search).get('tab')
  return tab ? propertyTabPath(nextPropertyId, tab) : propertyTabPath(nextPropertyId, '')
}
